import React from 'react';

import { cn } from 'shadcn/lib/utils';
import {
    COMPANY_SOURCE_BADGE_ABBREVIATIONS,
    COMPANY_SOURCE_BADGE_COLORS,
    getCompanySourceById,
    MM_FALLBACK_COMPANY_SOURCE_ID,
    resolveCompanySourceId,
} from 'shared/companySource';

/**
 * @param {Object} props
 * @param {number|string|null|undefined} [props.companySourceId]
 * @param {string|null|undefined} [props.companySource] - Company source code from API
 * @param {boolean} [props.showDisplayName=true]
 * @param {'before'|'after'} [props.badgePosition='before']
 * @param {string} [props.className]
 * @returns {JSX.Element|null}
 */
export function CompanySourceLabel({
    badgePosition = 'before',
    className,
    companySource,
    companySourceId,
    showDisplayName = true,
}) {
    const source = getCompanySourceById(
        resolveCompanySourceId({ companySourceId, companySource }) ?? MM_FALLBACK_COMPANY_SOURCE_ID,
    );

    if (!source) return null;

    const abbreviation =
        COMPANY_SOURCE_BADGE_ABBREVIATIONS[source.code] ?? source.displayName.slice(0, 2).toUpperCase();
    const badgeColor = COMPANY_SOURCE_BADGE_COLORS[source.code] ?? '#6B7280';

    const badge = (
        <span
            className="inline-flex h-6 min-w-[28px] shrink-0 items-center justify-center rounded px-1.5 text-xs font-bold text-white"
            style={{ backgroundColor: badgeColor }}
            aria-label={source.displayName}
        >
            {abbreviation}
        </span>
    );

    if (!showDisplayName) {
        return <span className={cn('inline-flex items-center', className)}>{badge}</span>;
    }

    const name = <span className="text-sm text-foreground">{source.displayName}</span>;

    return (
        <span className={cn('inline-flex items-center gap-2', className)}>
            {badgePosition === 'before' ? (
                <>
                    {badge}
                    {name}
                </>
            ) : (
                <>
                    {name}
                    {badge}
                </>
            )}
        </span>
    );
}

CompanySourceLabel.displayName = 'CompanySourceLabel';
